import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI, billingAPI } from '../../services/api';
import { CalendarCheck, Building2, Briefcase, Check, ArrowLeft, ArrowRight, Users, CreditCard, Sparkles, Plus, RefreshCw, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { t as tr, getLanguage, STORAGE_KEYS } from '../../i18n'
import { SERVICES, STORE_MODES, COUNTRIES, INCLUDED } from '../../config/services'
import { planPrice, planServicesText, planUsersText, featuredIndex, isHeading, fallbackPlanFeatures } from '../../config/plans'
import { PLAN_CURRENCY_STORAGE_KEY, planCurrencyForCountry } from '../../config/planCurrencies'
import PlanCurrencySelector from '../../components/PlanCurrencySelector'
import { getAffiliateRef } from '../../utils/affiliateRef';

// What exactly a bookings store books.
const BOOKING_KINDS = [
  { value: 'appointments', label: 'Appointments', desc: 'Clinics, salons, or any service you book by the slot', icon: CalendarCheck },
  { value: 'hotel', label: 'Hotel', desc: 'Room bookings by date and time slot', icon: Building2 },
  { value: 'office', label: 'Office / Coworking', desc: 'Desk or meeting-room bookings', icon: Briefcase },
];

const STEP_NAMES = { plan: 'Plan', services: 'Services', store: 'Store', account: 'Account' };
const STEPS = ['plan', 'services', 'store', 'account'];

const ARABIC = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
const PLAN_CACHE_KEY = 'rawaj_production_plans';
const PRODUCTION_PLAN_FALLBACK = [
  { id: 1, name: 'باقة الخدمة الواحدة', base_price_cents: 5900, currency: 'usd', interval: 'month', features: [] },
  { id: 2, name: 'باقة النمو', base_price_cents: 9900, currency: 'usd', interval: 'month', features: [] },
  { id: 3, name: 'الباقة الشاملة', base_price_cents: 14900, currency: 'usd', interval: 'month', features: [] },
];

const normalizePlans = livePlans => livePlans.map((p, index) => ({
  ...p,
  max_services: Object.prototype.hasOwnProperty.call(p, 'max_services') ? p.max_services : (index === 0 ? 1 : index === livePlans.length - 1 && livePlans.length >= 3 ? null : 2),
  max_users: Object.prototype.hasOwnProperty.call(p, 'max_users') ? p.max_users : (index === 0 ? 1 : index === livePlans.length - 1 && livePlans.length >= 3 ? 12 : 4),
  features: Array.isArray(p.features) && p.features.length ? p.features : fallbackPlanFeatures(index),
}));

const cachedPlans = () => {
  try {
    const value = JSON.parse(localStorage.getItem(PLAN_CACHE_KEY) || 'null');
    return Array.isArray(value) && value.length ? value : PRODUCTION_PLAN_FALLBACK;
  } catch { return PRODUCTION_PLAN_FALLBACK; }
};

// Sign-up: pick a plan, pick the services it allows (all in one store), name
// the store and its web address, then create the owner account.
export default function GetStartedPage() {
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState(() => normalizePlans(cachedPlans()));
  const [plansError, setPlansError] = useState(false);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    planId: Number(searchParams.get('plan')) || null,
    services: [],
    bookingKind: 'appointments',
    storeMode: 'both',
    country: 'OM',
    language: getLanguage() === 'ar' ? 'ar' : 'en',
    storeName: '',
    subdomain: '',
    phone: '',
    email: '',
    fullName: '',
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [passwordVisible, setPasswordVisible] = useState({ password: false, confirm: false });
  const [priceCountry, setPriceCountry] = useState(() => localStorage.getItem(PLAN_CURRENCY_STORAGE_KEY) || '');
  const displayCurrency = planCurrencyForCountry(priceCountry)?.currency;
  const changePriceCountry = value => {
    setPriceCountry(value);
    if (value) localStorage.setItem(PLAN_CURRENCY_STORAGE_KEY, value);
    else localStorage.removeItem(PLAN_CURRENCY_STORAGE_KEY);
  };
  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const loadPlans = async (retryStartup = true) => {
    setPlansError(false);
    const attempts = retryStartup ? 3 : 1;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        const { data } = await billingAPI.getPublicPlans();
        const livePlans = data.plans || [];
        // Keep the names and prices from production. The live server may
        // still use the earlier response shape, so fill only missing limits.
        if (!livePlans.length) throw new Error('No active production plans');
        localStorage.setItem(PLAN_CACHE_KEY, JSON.stringify(livePlans));
        setPlans(normalizePlans(livePlans));
        return;
      } catch (error) {
        if (attempt < attempts - 1) await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
      }
    }
    // Keep the last production response on screen during a transient outage.
    if (!plans?.length) setPlansError(true);
  };
  useEffect(() => { loadPlans(true); }, []);

  const plan = plans?.find(p => p.id === form.planId) || null;
  // Pricing is always the first decision. An empty production response is an
  // error state, not permission to create an account without a plan.
  const steps = STEPS;
  const current = steps[step - 1];
  const maxServices = plan ? plan.max_services : 1;

  const choosePlan = p => setForm(f => ({
    ...f,
    planId: p.id,
    // The biggest plan comes with everything ticked; others keep what fits.
    services: p.max_services == null ? (f.services.length ? f.services : SERVICES.map(s => s.id)) : f.services.slice(0, p.max_services),
  }));

  const toggleService = id => setForm(f => {
    if (f.services.includes(id)) return { ...f, services: f.services.filter(s => s !== id) };
    if (maxServices === 1) return { ...f, services: [id] };
    if (maxServices && f.services.length >= maxServices) {
      toast.error(tr('This plan includes up to {n} services. Pick a bigger plan for more.', { n: maxServices }));
      return f;
    }
    return { ...f, services: [...f.services, id] };
  });

  // The cheapest plan that can hold `count` services (for "add to my store").
  const planFor = count => (plans || []).filter(p => p.max_services == null || p.max_services >= count)
    .sort((a, b) => a.base_price_cents - b.base_price_cents)[0] || null;
  const addService = id => {
    if (form.services.includes(id)) return;
    const count = form.services.length + 1;
    if (maxServices == null || count <= maxServices) { setForm(f => ({ ...f, services: [...f.services, id] })); return; }
    const bigger = planFor(count);
    if (!bigger) { toast.error(tr('This plan includes up to {n} services. Pick a bigger plan for more.', { n: maxServices })); return; }
    setForm(f => ({ ...f, planId: bigger.id, services: [...f.services, id] }));
    toast.success(tr('Moved to the {plan} plan so you can add this service.', { plan: tr(bigger.name) }));
  };

  // Web address (subdomain): follows the store name until the owner edits it,
  // and is checked live so a name already in use is caught before signup.
  const [subdomainEdited, setSubdomainEdited] = useState(false);
  const [slugStatus, setSlugStatus] = useState({ state: 'idle' });
  const toSlug = value => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  const setStoreName = value => setForm(f => ({ ...f, storeName: value, subdomain: subdomainEdited ? f.subdomain : toSlug(value) }));
  // Web addresses can only use English letters; Arabic typed into the address
  // is dropped and the owner is told why instead of it silently vanishing.
  const [arabicTyped, setArabicTyped] = useState(false);
  const setSubdomain = value => {
    setSubdomainEdited(true);
    setArabicTyped(ARABIC.test(value));
    update('subdomain', value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-{2,}/g, '-').replace(/^-+/, '').slice(0, 40));
  };
  const arabicName = ARABIC.test(form.storeName) && !form.subdomain;
  const validSlugFormat = name => /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(name);

  const checkSubdomain = async name => {
    if (!name) return { state: 'bad', error: tr('Choose a web address for your store') };
    if (!validSlugFormat(name)) {
      const result = { state: 'bad', error: tr('Use 3-40 English letters, numbers or dashes for your web address.') };
      setSlugStatus(result);
      return result;
    }
    setSlugStatus({ state: 'checking' });
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const { data } = await authAPI.checkSubdomain(name);
        const result = { ...data, state: data.available ? 'ok' : 'bad' };
        setSlugStatus(result);
        return result;
      } catch (error) {
        if (attempt === 0) await new Promise(resolve => setTimeout(resolve, 400));
      }
    }
    // Valid syntax should not be blocked by a temporary availability outage.
    // Registration checks the same value transactionally and reports a taken
    // name with a suggestion if another business claimed it meanwhile.
    // Keep the normal positive UI state. The register endpoint performs the
    // authoritative transactional check again before creating anything.
    const result = { slug: name, available: true, state: 'ok', checkedAtSignup: true };
    setSlugStatus(result);
    return result;
  };

  useEffect(() => {
    const name = form.subdomain;
    if (!name) { setSlugStatus({ state: 'idle' }); return; }
    setSlugStatus(s => ({ ...s, state: 'checking' }));
    const timer = setTimeout(() => { checkSubdomain(name); }, 350);
    return () => clearTimeout(timer);
  }, [form.subdomain]);

  const next = async () => {
    if (current === 'plan' && !plan) { toast.error(tr('Choose a plan')); return; }
    if (current === 'services' && !form.services.length) { toast.error(tr('Choose at least one service')); return; }
    if (current === 'store' && !form.storeName.trim()) { toast.error(tr("Give your store a name")); return; }
    if (current === 'store' && form.phone.replace(/\D/g, '').length < 7) { toast.error(tr('Enter your phone number')); return; }
    if (current === 'store') {
      const checked = slugStatus.state === 'ok' && slugStatus.slug === form.subdomain ? slugStatus : await checkSubdomain(form.subdomain);
      if (checked.state !== 'ok') { toast.error(tr(checked.error || 'Choose a web address for your store')); return; }
    }
    setStep(s => s + 1);
  };
  const back = () => setStep(s => Math.max(1, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.username || !form.password) {
      toast.error(tr("Fill in your name, username, and password"));
      return;
    }
    if (form.phone.replace(/\D/g, '').length < 7) {
      toast.error(tr('Enter your phone number'));
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error(tr('Passwords do not match'));
      return;
    }
    // The first service picked is what the website opens with.
    const first = form.services[0];
    const businessType = first === 'store' ? 'ecommerce' : first === 'bookings' ? form.bookingKind : 'restaurant';
    try {
      setSubmitting(true);
      const { data } = await authAPI.register({
        planId: plan?.id || undefined,
        services: form.services,
        businessType,
        storeMode: form.services.includes('store') ? form.storeMode : undefined,
        country: form.country,
        language: form.language,
        storeName: form.storeName.trim(),
        subdomain: form.subdomain,
        phone: form.phone,
        email: form.email,
        fullName: form.fullName,
        username: form.username,
        password: form.password,
        // The Rawaj Partners marketer who sent this visitor, if any.
        affiliateRef: getAffiliateRef() || undefined,
      });
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      // The dashboard opens in the language picked for the store.
      localStorage.setItem(STORAGE_KEYS.admin, form.language);
      toast.success(tr("{name} is ready!", { name: data.store.name }));
      window.location.href = '/admin';
    } catch (error) {
      const message = error.response?.data?.error
        || (error.code === 'ECONNABORTED' ? 'The server took too long to respond. Please try again.' : null)
        || (error.request ? 'Could not reach the server. Check your connection and try again.' : null)
        || 'Could not create your account';
      toast.error(tr(message));
      // Someone took the name meanwhile: go back so the owner can change it.
      if (['SUBDOMAIN_TAKEN', 'SUBDOMAIN_INVALID'].includes(error.response?.data?.code)) {
        setSlugStatus({ state: 'bad', error: error.response.data.error, suggestion: error.response.data.suggestion });
        setStep(steps.indexOf('store') + 1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const nav = (
    <div className="flex gap-3 mt-8">
      {step > 1 && (
        <button type="button" onClick={back} className="flex-1 flex items-center justify-center gap-2 border border-slate-200 text-slate-700 font-medium py-3 rounded-xl hover:bg-slate-50">
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {tr("Back")}
        </button>
      )}
      <button type="button" onClick={next} className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white font-medium py-3 rounded-xl hover:bg-blue-700">
        {tr("Continue")} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className={`w-full ${['plan', 'services'].includes(current) ? 'max-w-5xl' : 'max-w-2xl'}`}>
        <Link to="/" className="flex items-center gap-1 text-slate-500 hover:text-slate-800 text-sm mb-6">
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {tr("Back to home")}
        </Link>

        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <ol className="mb-8 grid gap-2" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
            {steps.map((name, i) => (
              <li key={name} aria-current={i === step - 1 ? 'step' : undefined}>
                <div className={`h-1.5 rounded-full ${i < step ? 'bg-blue-600' : 'bg-slate-200'}`} />
                <span className={`mt-1.5 block truncate text-xs ${i === step - 1 ? 'font-semibold text-blue-700' : 'text-slate-500'}`}>{tr(STEP_NAMES[name])}</span>
              </li>
            ))}
          </ol>

          {!plans && !plansError && <p className="py-10 text-center text-slate-500">{tr('Loading...')}</p>}
          {!plans && plansError && (
            <div className="py-10 text-center">
              <p className="text-slate-700">{tr("We couldn't load the plans. Check your connection and try again.")}</p>
              <button type="button" onClick={() => loadPlans(false)} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700">
                <RefreshCw className="h-4 w-4" />{tr('Try again')}
              </button>
            </div>
          )}

          {plans && plans.length === 0 && (
            <div className="py-10 text-center" role="alert">
              <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-amber-50 text-amber-700">
                <CreditCard className="h-6 w-6" />
              </div>
              <h1 className="mt-4 text-xl font-bold text-slate-900">{tr('Plans are temporarily unavailable')}</h1>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">{tr('No active production plans were returned. Please try again in a moment.')}</p>
              <button type="button" onClick={() => loadPlans(false)} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                <RefreshCw className="h-4 w-4" />{tr('Reload plans')}
              </button>
            </div>
          )}

          {plans?.length > 0 && current === 'plan' && (
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">{tr("Choose your plan")}</h1>
              <p className="text-slate-500">{tr("Pick the plan that fits your business. You can change it any time.")}</p>
              <p className="mt-3 mb-6 flex items-start gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900 ring-1 ring-emerald-100">
                <CreditCard className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{tr("No payment today. Create your store first, then pay for your plan from your dashboard.")}</span>
              </p>
              <PlanCurrencySelector country={priceCountry} onChange={changePriceCountry} className="mb-6" />
              <div className="grid gap-4 md:grid-cols-3">
                {plans.map((p, i) => {
                  const on = form.planId === p.id;
                  return (
                    <button key={p.id} type="button" onClick={() => choosePlan(p)} aria-pressed={on}
                      className={`relative flex flex-col text-start rounded-2xl border-2 p-5 transition-all ${on ? 'border-blue-600 bg-blue-50/60 shadow-lg' : 'border-slate-200 hover:border-slate-300'}`}>
                      {i === featuredIndex(plans) && <span className="absolute -top-3 start-5 rounded-full bg-blue-700 px-3 py-0.5 text-xs font-semibold text-white">{tr('Most popular')}</span>}
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-lg font-bold text-slate-900">{tr(p.name)}</span>
                        <span className={`grid h-5 w-5 place-items-center rounded-full border ${on ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300'}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
                      </span>
                      <span className="mt-3 flex items-baseline gap-1">
                        <span className="text-3xl font-extrabold tracking-tight text-slate-900">{planPrice(p, displayCurrency)}</span>
                        <span className="text-sm text-slate-500">/ {tr(p.interval === 'year' ? 'year' : 'month')}</span>
                      </span>
                      <span className="mt-1 text-xs font-medium text-emerald-700">{tr('Pay later from your dashboard')}</span>
                      <span className="mt-4 text-sm font-semibold text-blue-700">{planServicesText(p)}</span>
                      <span className="mt-1 flex items-center gap-1.5 text-sm text-slate-700"><Users className="h-4 w-4 text-slate-400" />{planUsersText(p)}</span>
                      <ul className="mt-4 space-y-2 text-sm text-slate-600">
                        {(p.features || []).map((f, featureIndex) => isHeading(f)
                          ? <li key={`${f}-${featureIndex}`} className="mt-2 flex items-center gap-2 border-t border-slate-200 pt-3 text-xs font-bold uppercase tracking-wide text-blue-700"><Plus className="h-4 w-4" />{tr(f)}</li>
                          : <li key={`${f}-${featureIndex}`} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />{tr(f)}</li>)}
                      </ul>
                    </button>
                  );
                })}
              </div>
              <p className="mt-4 text-xs text-slate-500">{tr("WhatsApp marketing campaigns can be added to any plan for an extra monthly fee.")}</p>
              {nav}
            </div>
          )}

          {plans?.length > 0 && current === 'services' && (
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">{tr("What will your store offer?")}</h1>
              <p className="text-slate-500 mb-6">
                {maxServices === 1 ? tr("Your plan includes one service.") : maxServices ? tr("Pick up to {n}. They all live in the same store and the same admin.", { n: maxServices }) : tr("Your plan includes every service. They all live in the same store and the same admin.")}
                {plan && <span className="ms-1 font-medium text-slate-700">({tr(plan.name)})</span>}
              </p>
              <div className="space-y-4">
                {SERVICES.map(service => {
                  const on = form.services.includes(service.id);
                  const order = form.services.indexOf(service.id);
                  const others = SERVICES.filter(o => o.id !== service.id && !form.services.includes(o.id) && !(service.id === 'store' && o.id === 'bookings'));
                  return (
                    <div key={service.id} className={`overflow-hidden rounded-2xl border-2 transition-all ${on ? 'border-blue-600 shadow-md shadow-blue-600/10' : 'border-slate-200 hover:border-slate-300'}`}>
                      <button type="button" onClick={() => toggleService(service.id)} aria-pressed={on} aria-expanded={on}
                        className={`flex w-full items-start gap-3 p-4 text-start ${on ? 'bg-blue-50/70' : 'bg-white'}`}>
                        <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border ${on ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white'}`}>{on && <Check className="h-4 w-4" />}</span>
                        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${on ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}><service.icon className="h-5 w-5" /></span>
                        <span className="flex-1">
                          <span className="font-semibold text-slate-900">{tr(service.title)}</span>
                          {on && form.services.length > 1 && order === 0 && <span className="ms-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">{tr('Home page')}</span>}
                          <span className="block text-sm text-slate-500">{tr(service.desc)}</span>
                          {!on && <span className="mt-1.5 block text-xs text-slate-400">{service.includes.slice(0, 3).map(i => tr(i)).join(' · ')} {tr('+{n} more', { n: service.includes.length - 3 })}</span>}
                        </span>
                      </button>
                      {on && (
                        <div className="space-y-5 border-t border-blue-100 bg-white p-4">
                          <div>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{tr("What's included")}</p>
                            <ul className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
                              {service.includes.map(item => <li key={item} className="flex gap-2 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{tr(item)}</li>)}
                            </ul>
                          </div>
                          {service.id === 'store' && (
                            <div className="space-y-5">
                              <div className="rounded-2xl border border-violet-200 bg-violet-50/70 p-4">
                                <div className="flex items-start gap-3">
                                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-violet-100 text-violet-700"><CalendarCheck className="h-5 w-5" /></span>
                                  <div className="flex-1">
                                    <p className="font-semibold text-slate-900">{tr('Do you also want reservations on your online store?')}</p>
                                    <p className="mt-1 text-sm leading-6 text-slate-600">{tr('Customers can shop and reserve appointments, tables, rooms or services from the same website.')}</p>
                                  </div>
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-2" role="radiogroup" aria-label={tr('Add reservations to the online store?')}>
                                  <button type="button" role="radio" aria-checked={form.services.includes('bookings')} onClick={() => addService('bookings')}
                                    className={`min-h-11 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${form.services.includes('bookings') ? 'border-violet-600 bg-violet-600 text-white' : 'border-violet-200 bg-white text-slate-700 hover:border-violet-400'}`}>
                                    {tr('Yes, add reservations')}
                                  </button>
                                  <button type="button" role="radio" aria-checked={!form.services.includes('bookings')} onClick={() => setForm(f => ({ ...f, services: f.services.filter(id => id !== 'bookings') }))}
                                    className={`min-h-11 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${!form.services.includes('bookings') ? 'border-slate-500 bg-white text-slate-900 ring-1 ring-slate-300' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'}`}>
                                    {tr('No, products only')}
                                  </button>
                                </div>
                              </div>
                              <div>
                                <p className="mb-2 text-sm font-medium text-slate-700">{tr('How do you sell?')}</p>
                                <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label={tr('How do you sell?')}>
                                  {STORE_MODES.map(mode => (
                                    <button key={mode.value} type="button" role="radio" aria-checked={form.storeMode === mode.value} onClick={() => update('storeMode', mode.value)}
                                      className={`rounded-xl border p-3 text-start text-sm ${form.storeMode === mode.value ? 'border-blue-600 bg-blue-50/60 ring-1 ring-blue-600' : 'border-slate-200 hover:border-slate-300'}`}>
                                      <mode.icon className="mb-1 h-4 w-4 text-blue-700" />
                                      <span className="block font-medium text-slate-900">{tr(mode.label)}</span>
                                      <span className="mt-0.5 block text-xs text-slate-500">{tr(mode.desc)}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                          {service.id === 'bookings' && (
                            <div>
                              <p className="mb-2 text-sm font-medium text-slate-700">{tr('What do customers book?')}</p>
                              <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label={tr('What do customers book?')}>
                                {BOOKING_KINDS.map(kind => (
                                  <button key={kind.value} type="button" role="radio" aria-checked={form.bookingKind === kind.value} onClick={() => update('bookingKind', kind.value)}
                                    className={`rounded-xl border p-3 text-start text-sm ${form.bookingKind === kind.value ? 'border-blue-600 bg-blue-50/60 ring-1 ring-blue-600' : 'border-slate-200 hover:border-slate-300'}`}>
                                    <kind.icon className="mb-1 h-4 w-4 text-blue-700" />
                                    <span className="block font-medium text-slate-900">{tr(kind.label)}</span>
                                    <span className="mt-0.5 block text-xs text-slate-500">{tr(kind.desc)}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {others.length > 0 && (
                            <div className="rounded-xl bg-slate-50 p-3">
                              <p className="text-sm font-medium text-slate-700">{tr('Want more in the same store?')}</p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {others.map(o => {
                                  const fits = maxServices == null || form.services.length < maxServices;
                                  const bigger = fits ? null : planFor(form.services.length + 1);
                                  if (!fits && !bigger) return null;
                                  return (
                                    <button key={o.id} type="button" onClick={() => addService(o.id)}
                                      className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:border-blue-600 hover:bg-blue-50">
                                      <Plus className="h-4 w-4" /><o.icon className="h-4 w-4" />{tr(o.add)}
                                      {bigger && <span className="text-xs font-normal text-slate-500">· {tr('with the {plan} plan', { plan: tr(bigger.name) })}</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {form.services.length > 1 && <p className="mt-3 text-xs text-slate-500">{tr("The first one you picked opens as your home page. You can change this later in Settings.")}</p>}
              <div className="mt-6 rounded-xl bg-slate-50 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Sparkles className="h-4 w-4 text-blue-700" />{tr('Included in every plan')}</p>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {INCLUDED.map(item => <li key={item} className="flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200"><Check className="h-3.5 w-3.5 text-blue-700" />{tr(item)}</li>)}
                </ul>
              </div>
              {nav}
            </div>
          )}

          {plans && current === 'store' && (
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">{tr("Name your store")}</h1>
              <p className="text-slate-500 mb-6">{tr("This becomes your subdomain and shows to customers.")}</p>
              <label htmlFor="storeName" className="block text-sm font-medium text-slate-700 mb-1.5">{tr("Store name")}</label>
              <input id="storeName" autoFocus required placeholder={tr("e.g., Bella Vista Cafe")} value={form.storeName}
                onChange={e => setStoreName(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <label htmlFor="subdomain" className="block text-sm font-medium text-slate-700 mt-5 mb-1.5">{tr("Your web address")}</label>
              <div dir="ltr" className={`flex items-stretch rounded-xl border overflow-hidden focus-within:ring-2 ${slugStatus.state === 'bad' ? 'border-red-300 focus-within:ring-red-200' : 'border-slate-200 focus-within:ring-blue-500'}`}>
                <span className="px-3 grid place-items-center bg-slate-50 text-slate-500 text-sm border-e border-slate-200">https://</span>
                <input id="subdomain" value={form.subdomain} spellCheck={false} autoCapitalize="none" placeholder="my-store"
                  onChange={e => setSubdomain(e.target.value)}
                  className="flex-1 min-w-0 px-3 py-3 outline-none" />
                <span className="px-3 grid place-items-center bg-slate-50 text-slate-500 text-sm border-s border-slate-200 whitespace-nowrap">.{slugStatus.domain || 'yourdomain.com'}</span>
              </div>
              {/* One keyed element per message: browser translators rewrite the
                  text, and swapping loose text nodes under them crashes React. */}
              <p className={`text-sm mt-1.5 ${slugStatus.state === 'bad' ? 'text-red-600' : slugStatus.state === 'ok' ? 'text-emerald-700' : 'text-slate-500'}`} aria-live="polite">
                <span key={slugStatus.state}>{tr({
                  checking: 'Checking...',
                  ok: 'Available - this will be your website address.',
                  bad: slugStatus.error || 'This name is not available',
                }[slugStatus.state] || 'English letters, numbers and dashes only.')}</span>
              </p>
              {(arabicTyped || arabicName) && (
                <p className="text-sm mt-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">
                  {tr(arabicTyped
                    ? 'Arabic letters are not allowed in the web address. Please use English letters, numbers and dashes only (for example: al-bahr).'
                    : 'Your store name can be in Arabic. Please type the web address above in English letters (for example: al-bahr).')}
                </p>
              )}
              {slugStatus.state === 'bad' && slugStatus.suggestion && (
                <button type="button" onClick={() => { setSubdomainEdited(true); update('subdomain', slugStatus.suggestion); }} className="text-sm font-medium text-blue-700 hover:underline mt-1">
                  {tr('Use "{name}" instead', { name: slugStatus.suggestion })}
                </button>
              )}
              <div className="mt-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">{tr("Contact email (optional)")}</label>
                  <input id="email" type="email" dir="ltr" autoComplete="email" value={form.email}
                    onChange={e => update('email', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-500">{tr("Optional. You can change it later in Settings.")}</p>
              <div className="mt-5">
                <label htmlFor="storePhone" className="block text-sm font-medium text-slate-700 mb-1.5">{tr("Phone number (required)")} <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id="storePhone" required aria-required="true" type="tel" dir="ltr" inputMode="tel" autoComplete="tel" placeholder="+968 9XXX XXXX" value={form.phone}
                  onChange={e => update('phone', e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-start focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                <p className="mt-1.5 text-xs text-slate-500">{tr('Required for your business account, customer contact and receipts.')}</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 mt-5">
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-slate-700 mb-1.5">{tr("Country")}</label>
                  <select id="country" value={form.country} onChange={e => update('country', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{tr(c.name)} ({c.currency})</option>)}
                  </select>
                  <p className="mt-1 text-xs text-slate-500">{tr("Sets your prices' currency and your time zone.")}</p>
                </div>
                <div>
                  <label htmlFor="language" className="block text-sm font-medium text-slate-700 mb-1.5">{tr("Main language")}</label>
                  <select id="language" value={form.language} onChange={e => update('language', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="ar">العربية</option>
                    <option value="en">English</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">{tr("Customers can still switch language on your website.")}</p>
                </div>
              </div>
              {nav}
            </div>
          )}

          {plans?.length > 0 && current === 'account' && (
            <form onSubmit={submit}>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">{tr("Create your account")}</h1>
              <p className="text-slate-500 mb-6">{tr("This is how you'll log in and manage")} {form.storeName || tr("your store")}.</p>
              <div className="mb-5 rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-blue-950"><ShieldCheck className="h-4 w-4" />{tr('Your phone number is required')}</p>
                <p className="mt-1 text-sm leading-6 text-blue-900/75">{tr('We use it as the main contact for your business and show it to customers on your website and receipts.')}</p>
              </div>
              <div className="space-y-3">
                <label htmlFor="ownerName" className="block text-sm font-medium text-slate-700">{tr('Full name')} <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id="ownerName" required placeholder={tr("Full name")} value={form.fullName} autoComplete="name" onChange={e => update('fullName', e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700">{tr("Phone number (required)")} <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id="phone" required aria-required="true" type="tel" dir="ltr" inputMode="tel" autoComplete="tel" placeholder="+968 9XXX XXXX" value={form.phone} onChange={e => update('phone', e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-start focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                <label htmlFor="username" className="block text-sm font-medium text-slate-700">{tr('Username')} <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id="username" required placeholder={tr("Username")} value={form.username} autoComplete="username" onChange={e => update('username', e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">{tr('Password')} <span className="text-red-600" aria-hidden="true">*</span></label>
                <div className="relative">
                  <input id="password" required minLength={6} type={passwordVisible.password ? 'text' : 'password'} placeholder={tr("Password (min. 6 characters)")} value={form.password} autoComplete="new-password" onChange={e => update('password', e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 pe-12 focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                  <button type="button" onClick={() => setPasswordVisible(v => ({ ...v, password: !v.password }))} aria-label={tr(passwordVisible.password ? 'Hide password' : 'Show password')} aria-pressed={passwordVisible.password}
                    className="absolute inset-y-0 end-0 grid w-12 cursor-pointer place-items-center rounded-e-xl text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                    {passwordVisible.password ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">{tr('Confirm password')} <span className="text-red-600" aria-hidden="true">*</span></label>
                <div className="relative">
                  <input id="confirmPassword" required minLength={6} type={passwordVisible.confirm ? 'text' : 'password'} placeholder={tr('Enter the password again')} value={form.confirmPassword} autoComplete="new-password" onChange={e => update('confirmPassword', e.target.value)}
                    aria-invalid={!!form.confirmPassword && form.password !== form.confirmPassword}
                    className={`w-full rounded-xl border px-4 py-3 pe-12 focus:border-transparent focus:ring-2 ${form.confirmPassword && form.password !== form.confirmPassword ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:ring-blue-500'}`} />
                  <button type="button" onClick={() => setPasswordVisible(v => ({ ...v, confirm: !v.confirm }))} aria-label={tr(passwordVisible.confirm ? 'Hide password' : 'Show password')} aria-pressed={passwordVisible.confirm}
                    className="absolute inset-y-0 end-0 grid w-12 cursor-pointer place-items-center rounded-e-xl text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                    {passwordVisible.confirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {form.confirmPassword && form.password !== form.confirmPassword && <p className="text-sm font-medium text-red-600" role="alert">{tr('Passwords do not match')}</p>}
              </div>
              <div className="mt-6 rounded-xl border border-slate-200 p-4 text-sm">
                <p className="font-semibold text-slate-900">{tr('Your summary')}</p>
                <dl className="mt-3 space-y-2 text-slate-600">
                  {plan && <div className="flex justify-between gap-3"><dt>{tr('Plan')}</dt><dd className="text-end font-medium text-slate-900">{tr(plan.name)} · <span dir="ltr">{planPrice(plan)}</span> / {tr(plan.interval === 'year' ? 'year' : 'month')}</dd></div>}
                  <div className="flex justify-between gap-3"><dt>{tr('Services')}</dt><dd className="text-end font-medium text-slate-900">{form.services.map(id => tr(SERVICES.find(s => s.id === id)?.title)).join(' + ')}</dd></div>
                  {form.services.includes('store') && <div className="flex justify-between gap-3"><dt>{tr('How do you sell?')}</dt><dd className="text-end font-medium text-slate-900">{tr(STORE_MODES.find(m => m.value === form.storeMode)?.label)}</dd></div>}
                  {form.services.includes('bookings') && <div className="flex justify-between gap-3"><dt>{tr('What do customers book?')}</dt><dd className="text-end font-medium text-slate-900">{tr(BOOKING_KINDS.find(k => k.value === form.bookingKind)?.label)}</dd></div>}
                  <div className="flex justify-between gap-3"><dt>{tr('Country')}</dt><dd className="text-end font-medium text-slate-900">{tr(COUNTRIES.find(c => c.code === form.country)?.name)}</dd></div>
                  {plan && <div className="flex justify-between gap-3 border-t border-slate-100 pt-2"><dt className="font-semibold text-slate-900">{tr('To pay today')}</dt><dd className="font-bold text-emerald-700" dir="ltr">{planPrice({ ...plan, base_price_cents: 0 })}</dd></div>}
                </dl>
                {plan && <p className="mt-3 text-xs text-slate-500">{tr("You'll pay for your plan from your dashboard after your store is created.")}</p>}
              </div>
              <div className="flex gap-3 mt-8">
                <button type="button" onClick={back} className="flex-1 flex items-center justify-center gap-2 border border-slate-200 text-slate-700 font-medium py-3 rounded-xl hover:bg-slate-50">
                  <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {tr("Back")}
                </button>
                <button type="submit" disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white font-medium py-3 rounded-xl hover:bg-green-700 disabled:opacity-50">
                  <Check className="w-4 h-4" /> {submitting ? tr("Creating...") : tr("Create my store")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
